import React, { useState, useEffect, useMemo } from "react";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  UserIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
  EyeIcon,
  PencilIcon,
  FireIcon,
  BoltIcon,
  LightBulbIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  BuildingOffice2Icon,
  WrenchScrewdriverIcon,
  UserCircleIcon,
  EllipsisHorizontalCircleIcon,
  LinkIcon,
  CheckBadgeIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline";
import apiService, { SuperAdminUser } from "../../services/api";
import Toast from "../../components/Toast";
import { dateFormatDynamic } from "../../utils/datetime";
import TablePagination from "../company/JobManagement/Components/TablePagination";
import { BulkUpdateTicketRequest } from "./model/assign_close_ticket_request";
import {
  SupportTicketPriority,
  SupportTicketStatus,
} from "./model/support_ticket_enums";
import { UpdateSupportTicketPayload } from "./model/update_support_ticket_playload";
import { DateFormats } from "../../utils/formatters";
import { UserMinusIcon } from "lucide-react";

interface SupportTicket {
  id: string;
  ticket_number: string;
  contact_name: string;
  contact_email: string;
  subject: string;
  description: string;
  category: string;
  priority: "LOW" | "MEDIUM" | "HIGH" | "URGENT" | "CRITICAL";
  status:
    | "OPEN"
    | "IN_PROGRESS"
    | "WAITING_FOR_CUSTOMER"
    | "WAITING_FOR_INTERNAL"
    | "RESOLVED"
    | "CLOSED";
  source:
    | "LANDING_PAGE"
    | "JOB_SEEKER_PORTAL"
    | "COMPANY_PORTAL"
    | "EMAIL"
    | "PHONE"
    | "CHAT"
    | "INTERNAL";
  assigned_to?: string;
  created_at: string;
  updated_at: string;
  reply_count: number;
  last_reply_at?: string;
  assigned_to_email?: string;
  assigned_first_name?: string;
  assigned_last_name?: string;
  assigned_to_role?: string;
  due_date?: string | null;
  internal_notes? : string | null;

}

interface TicketStats {
  total_tickets: number;
  open_tickets: number;
  in_progress_tickets: number;
  waiting_for_customer_tickets: number;
  resolved_tickets: number;
  closed_tickets: number;
  overdue_tickets: number;
  unassigned_tickets: number;
  avg_response_time_hours?: number;
  avg_resolution_time_hours?: number;
  satisfaction_rating?: number;
  tickets_by_source: Record<string, number>;
  tickets_by_category: Record<string, number>;
  tickets_by_priority: Record<string, number>;
  open_tickets_by_priority: Record<string, number>;
}

const getAssigneeName = (user: SuperAdminUser) => {
  const fullName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || user.email;
};

const formatRoleLabel = (role: string) => {
  return role
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const SupportTickets: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "all" | "landing" | "job_seekers" | "companies"
  >("all");
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [assigneeInput, setAssigneeInput] = useState("");
  const [createdAfter, setCreatedAfter] = useState("");
  const [createdBefore, setCreatedBefore] = useState("");
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignSelectedTicket, setShowAssignSelectedTicket] =
    useState(false);
  const [showCloseTicket, setShowCloseTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
    null,
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [getAllTicketTotalCount, setTotalCount] = useState(0);

  const SOURCE_MAP: Record<string, string> = {
    landing: "LANDING_PAGE",
    job_seekers: "JOB_SEEKER_PORTAL",
    companies: "COMPANY_PORTAL",
  };

  // Edit form state
  const [editPriority, setEditPriority] = useState<string>("");
  const [editStatus, setEditStatus] = useState<string>("");
  const [editAssigneeRole, setEditAssigneeRole] = useState<string>("");
  const [editAssigneeUserId, setEditAssigneeUserId] = useState<string>("");
  const [assigneeUsers, setAssigneeUsers] = useState<SuperAdminUser[]>([]);
  const [loadingAssigneeUsers, setLoadingAssigneeUsers] = useState(false);
  const [assigneeUsersError, setAssigneeUsersError] = useState<string | null>(
    null,
  );
  const [editNotes, setEditNotes] = useState<string>("");
  const [editDueDate, setEditDueDate] = useState<string>("");
  const [updatingTicket, setUpdatingTicket] = useState(false);
  const [notifyCreator, setNotifyCreator] = useState(false);
  const [showMyTickets, setShowMyTickets] = useState(false);
  const [isSearchApplied, setIsSearchApplied] = useState(false);
  const [showOverdueTickets, setShowOverdueTickets] = useState(false);
  const authUser = JSON.parse(localStorage.getItem("user") || "null");
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
    isVisible: boolean;
  }>({
    message: "",
    type: "success",
    isVisible: false,
  });
  const closeToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  const filteredAssigneeUsers = useMemo(() => {
    const activeUsers = assigneeUsers.filter(
      (user) =>
        user.is_active !== false && user.super_admin_role === editAssigneeRole,
    );

    if (selectedTicket?.assigned_to_email) {
      const assignedUser = assigneeUsers.find(
        (user) =>
          user.email.toLowerCase() ===
          selectedTicket.assigned_to_email?.toLowerCase(),
      );
      if (assignedUser && !activeUsers.some((u) => u.id === assignedUser.id)) {
        return [...activeUsers, assignedUser];
      }
    }
    return activeUsers;
  }, [assigneeUsers, editAssigneeRole, selectedTicket]);
  const assigneeRoleOptions = useMemo(
    () =>
      Array.from(
        new Set(
          assigneeUsers.map((user) => user.super_admin_role).filter(Boolean),
        ),
      ),
    [assigneeUsers],
  );
  const selectedAssigneeUser = assigneeUsers.find(
    (user) => user.id === editAssigneeUserId,
  );
  const editAssigneeEmail = selectedAssigneeUser?.email || "";

  const fetchAssigneeUsers = async () => {
    try {
      setLoadingAssigneeUsers(true);
      setAssigneeUsersError(null);

      const data = await apiService.getAllUsers();
      setAssigneeUsers(data.users || []);
    } catch (error) {
      console.error("Error loading assignee users:", error);
      setAssigneeUsers([]);
      setAssigneeUsersError(
        error instanceof Error
          ? error.message
          : "Failed to load assignee users",
      );
    } finally {
      setLoadingAssigneeUsers(false);
    }
  };

  // Fetch data from API
  const fetchTicketsAndStats = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("No access token found");
        return;
      }

      // Fetch ticket statistics
      const statsResponse = await apiService.get(`/support-tickets/stats?get_my_assignments=${showMyTickets}`);

      if (statsResponse) {
        const statsData = await statsResponse.data;
        setStats(statsData);
        console.log("nnn", statsData);
      } else {
        console.error("Failed to fetch ticket stats:");
        // Fallback to empty stats if API fails
        setStats({
          total_tickets: 0,
          open_tickets: 0,
          in_progress_tickets: 0,
          waiting_for_customer_tickets: 0,
          resolved_tickets: 0,
          closed_tickets: 0,
          overdue_tickets: 0,
          unassigned_tickets: 0,
          avg_response_time_hours: 0,
          avg_resolution_time_hours: 0,
          satisfaction_rating: 0,
          tickets_by_source: {},
          tickets_by_category: {},
          tickets_by_priority: {},
          open_tickets_by_priority: {},
        });
      }

      // Ticket list is loaded by fetchTickets via useEffect
    } catch (error) {
      console.error("Error fetching support ticket data:", error);
      // Set fallback data on error
      setStats({
        total_tickets: 0,
        open_tickets: 0,
        in_progress_tickets: 0,
        waiting_for_customer_tickets: 0,
        resolved_tickets: 0,
        closed_tickets: 0,
        overdue_tickets: 0,
        unassigned_tickets: 0,
        avg_response_time_hours: 0,
        avg_resolution_time_hours: 0,
        satisfaction_rating: 0,
        tickets_by_source: {},
        tickets_by_category: {},
        tickets_by_priority: {},
        open_tickets_by_priority: {},
      });
    } finally {
      setLoading(false);
    }
  };
  //created_after
  const fetchTickets = async (
    page = 1,
    tab = activeTab,
    limit = rowsPerPage,
    status = statusFilter,
    priority = priorityFilter,
    source = sourceFilter,
    category = categoryFilter,
    assignee = assigneeSearch,
    search = searchTerm,
    createAfter = createdAfter,
    createBefore = createdBefore,
    myTickets = showMyTickets,
    showOverDue = showOverdueTickets
  ) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("No access token found");
        return;
      }

      const skip = (page - 1) * limit;
      const params = new URLSearchParams({
        skip: String(skip),
        limit: String(limit),
        sort_by: "created_at",
        sort_order: "desc",
      });
      if (SOURCE_MAP[tab]) params.set("source", SOURCE_MAP[tab]);
      if(showOverDue) params.set("show_over_due", String(showOverDue))
      if (status) params.set("status", status);
      if (priority) params.set("priority", priority);
      if (source && !SOURCE_MAP[tab]) params.set("source", source);
      if (category) params.set("category", category);
      if (assignee) params.set("assigned_to", assignee);
      params.set("get_my_assignments", String(myTickets));
      if (search) params.set("search", search);
      if (createAfter) params.set("created_after", `${createAfter} 00:00:00`);
      if (createBefore)
        params.set("created_before", `${createBefore} 23:59:59`);

      const ticketsResponse = await apiService.get(
        `/support-tickets/?${params.toString()}`,
      );
      setTotalCount(Number(ticketsResponse.headers["x-total-count"] ?? 0));
      if (ticketsResponse) {
        const ticketsData = await ticketsResponse.data;
        setTickets(
          Array.isArray(ticketsData) ? ticketsData : ticketsData.items ?? [],
        );
      } else {
        setTickets([]);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setTickets([]);
    }
  };
  useEffect(() => {
    fetchTicketsAndStats();
  }, [showMyTickets]);
  const handleSearch = () => {
    setCurrentPage(1);
    setSearchTerm(searchInput.trim());
    setIsSearchApplied(true);
  };
  const handleClear = () => {
    setSearchInput("");
    setSearchTerm("");
    setIsSearchApplied(false);

    fetchTickets(1);
  };
  useEffect(() => {
    if (!loading) {
      fetchTickets(
        currentPage,
        activeTab,
        rowsPerPage,
        statusFilter,
        priorityFilter,
        sourceFilter,
        categoryFilter,
        assigneeSearch,
        searchTerm,
        createdAfter,
        createdBefore,
        showMyTickets,
        showOverdueTickets
      );
    }
  }, [
    currentPage,
    activeTab,
    rowsPerPage,
    loading,
    statusFilter,
    priorityFilter,
    sourceFilter,
    categoryFilter,
    assigneeSearch,
    searchTerm,
    createdAfter,
    createdBefore,
    showMyTickets,
    showOverdueTickets
  ]);

  useEffect(() => {
    if (
      !showEditModal ||
      !selectedTicket?.assigned_to_email ||
      assigneeUsers.length === 0
    ) {
      return;
    }

    const assignedUser = assigneeUsers.find(
      (user) =>
        user.email.toLowerCase() ===
        selectedTicket.assigned_to_email?.toLowerCase(),
    );

    if (assignedUser) {
      setEditAssigneeRole(assignedUser.super_admin_role);
      setEditAssigneeUserId(assignedUser.id);
    }
  }, [showEditModal, selectedTicket, assigneeUsers]);

  useEffect(() => {
    if (
      editAssigneeUserId &&
      !filteredAssigneeUsers.some((user) => user.id === editAssigneeUserId)
    ) {
      setEditAssigneeUserId("");
    }
  }, [editAssigneeRole, editAssigneeUserId, filteredAssigneeUsers]);

  // Handle ticket actions
  const handleViewTicket = async (ticket: SupportTicket) => {
    try {
      // Fetch full ticket details including description
      const response = await apiService.get(`/support-tickets/${ticket.id}`);

      if (response) {
        const fullTicketData = await response.data;
        setSelectedTicket(fullTicketData);
      } else {
        console.error("Failed to fetch ticket details");
        // Fallback to using the list data if API fails
        setSelectedTicket(ticket);
      }
    } catch (error) {
      console.error("Error fetching ticket details:", error);
      // Fallback to using the list data if API fails
      setSelectedTicket(ticket);
    }

    setShowViewModal(true);
  };

  const handleEditTicket = async (ticket: SupportTicket) => {
    setNotifyCreator(false);
    try {
      const response = await apiService.get(`/support-tickets/${ticket.id}`);

      const fullTicketData = response?.data || ticket;
      setSelectedTicket(fullTicketData);
      setEditPriority(fullTicketData.priority);
      setEditStatus(fullTicketData.status);
      setEditAssigneeRole("");
      setEditAssigneeUserId("");
      setEditNotes(fullTicketData.internal_notes || "");
      setEditDueDate(
        dateFormatDynamic(
          fullTicketData.due_date || null,
          DateFormats.yearMonthDay,
        ),
      );
    } catch (error) {
      console.error("Error loading ticket details for edit:", error);
      setSelectedTicket(ticket);
      setEditPriority(ticket.priority);
      setEditStatus(ticket.status);
      setEditAssigneeRole("");
      setEditAssigneeUserId("");
      setEditNotes("");
      setEditDueDate(
        dateFormatDynamic(ticket.due_date || null, DateFormats.yearMonthDay),
      );
    }

    setShowEditModal(true);
    if (assigneeUsers.length === 0) {
      await fetchAssigneeUsers();
    }
  };

  // Handle updating ticket
  const handleUpdateTicket = async () => {
    if (!selectedTicket) return;

    if (!editAssigneeRole) {
      setToast({ message: "Please select a Super Admin Role.", type: "error", isVisible: true });
      return;
    }
    if (!editAssigneeUserId) {
      setToast({ message: "Please select an Assignee.", type: "error", isVisible: true });
      return;
    }

    setUpdatingTicket(true);
    try {
      const response = await apiService.put(
        `/support-tickets/${selectedTicket.id}`,
        UpdateSupportTicketPayload({
          priority: editPriority,
          status: editStatus,
          assignedToEmail: editAssigneeEmail || null,
          internalNotes: editNotes.trim() || null,
          dueDate: editDueDate || undefined,
          enableCreatorUpdate: notifyCreator,
        }),
      );

      if (response) {
        setToast({
          message: "Ticket updated successfully!",
          type: "success",
          isVisible: true,
        });
        setShowEditModal(false);
        // Refresh the ticket list
        await fetchTickets(currentPage, activeTab);
        setSelectedTicket(null);
      } else {
        setToast({
          message: "Failed to update ticket",
          type: "error",
          isVisible: true,
        });
      }
    } catch (error) {
      setToast({
        message: "Failed to update ticket",
        type: "error",
        isVisible: true,
      });
      console.error("Error updating ticket:", error);
    } finally {
      setUpdatingTicket(false);
    }
  };

  const clearAssigneTicketData = () => {
    setEditAssigneeUserId("");
    setEditDueDate("");
    setEditAssigneeRole("");
    setNotifyCreator(false);
  };

  const handleAssignCloseTicket = async () => {
    if (!selectedTickets || selectedTickets.length === 0) return;

    if (!showCloseTicket) {
      if (!editAssigneeRole) {
        setToast({ message: "Please select a Super Admin Role.", type: "error", isVisible: true });
        return;
      }
      if (!editAssigneeUserId) {
        setToast({ message: "Please select an Assignee.", type: "error", isVisible: true });
        return;
      }
    }

    setUpdatingTicket(true);
    try {
      const closePayload: BulkUpdateTicketRequest = {
        ticket_ids: selectedTickets,
        status: "CLOSED",
        enable_creator_update: notifyCreator,
      };

      const assignPayload: BulkUpdateTicketRequest = {
        ticket_ids: selectedTickets,
        assigned_to: editAssigneeUserId,
        due_date: editDueDate ? `${editDueDate}T00:00:00Z` : undefined,
      };

      const response = await apiService.post(
        `/support-tickets/bulk-update`,
        showCloseTicket ? closePayload : assignPayload,
      );

      if (response) {
        setToast({
          message: "Ticket updated successfully!",
          type: "success",
          isVisible: true,
        });
        setShowCloseTicket(false);
        // Refresh the ticket list
        await fetchTickets(currentPage, activeTab);
        setShowEditModal(false);
        setSelectedTickets([]);
        setSelectedTicket(null);
        setShowAssignSelectedTicket(false);
        setNotifyCreator(false);
      } else {
        setToast({
          message: "Failed to update ticket",
          type: "error",
          isVisible: true,
        });
      }
    } catch (error) {
      setToast({
        message: "Failed to update ticket",
        type: "error",
        isVisible: true,
      });
      console.error("Error updating ticket:", error);
    } finally {
      setUpdatingTicket(false);
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case "LANDING_PAGE":
        return <GlobeAltIcon className="h-4 w-4" />;
      case "COMPANY_PORTAL":
        return <BuildingOfficeIcon className="h-4 w-4" />;
      case "JOB_SEEKER_PORTAL":
        return <UserIcon className="h-4 w-4" />;
      default:
        return <ChatBubbleLeftRightIcon className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "CRITICAL":
        return "bg-red-100 text-red-800 border-red-200";
      case "URGENT":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "HIGH":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "MEDIUM":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "LOW":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case "CRITICAL":
        return <FireIcon className="h-4 w-4" />;
      case "URGENT":
        return <BoltIcon className="h-4 w-4" />;
      case "HIGH":
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "OPEN":
        return "bg-green-100 text-green-800 border-green-200";
      case "IN_PROGRESS":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "WAITING_FOR_CUSTOMER":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "WAITING_FOR_INTERNAL":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "RESOLVED":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "CLOSED":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "OPEN":
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case "IN_PROGRESS":
        return <ClockIcon className="h-4 w-4" />;
      case "RESOLVED":
        return <CheckCircleIcon className="h-4 w-4" />;
      case "CLOSED":
        return <XMarkIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  // Filtering is handled server-side via API params
  const filteredTickets = tickets;

  const formatTimeSince = (dateString: string) => {
    // If the API sends UTC without a timezone, append "Z"
    const date = new Date(
      dateString.endsWith("Z") ? dateString : `${dateString}Z`,
    );

    const now = new Date();

    const diffMs = now.getTime() - date.getTime();

    const diffInMinutes = Math.floor(diffMs / (1000 * 60));
    const diffInHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}w ago`;
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading support tickets...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
        </div>
        { authUser.show_total_tickets && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">My Tickets</span>
          <button
            type="button"
            onClick={() => { setShowMyTickets((prev) => !prev); setCurrentPage(1); }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              showMyTickets ? "bg-blue-600" : "bg-gray-200"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                showMyTickets ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Tickets
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total_tickets}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <ExclamationTriangleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Open Tickets
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.open_tickets}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <ClockIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Avg Resolution Time
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.avg_resolution_time_hours
                    ? `${stats.avg_resolution_time_hours.toFixed(1)}h`
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-lg">
                <UserMinusIcon className="h-6 w-6 text-gray-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Unassigned</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.unassigned_tickets}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Overdue</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.overdue_tickets}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Priority and Status Breakdown for Open Tickets */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Half - Open Tickets by Priority */}
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Open vs Total Tickets by Priority
            </h3>
            <div className="flex flex-wrap gap-4">
              {/* Critical */}
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <FireIcon className="h-4 w-4 text-red-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Critical</p>
                  <p className="text-lg font-bold text-red-600">
                    {stats.open_tickets_by_priority?.CRITICAL || 0} /{" "}
                    {stats.tickets_by_priority?.CRITICAL || 0}
                  </p>
                </div>
              </div>

              {/* Urgent */}
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <BoltIcon className="h-4 w-4 text-orange-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Urgent</p>
                  <p className="text-lg font-bold text-orange-600">
                    {stats.open_tickets_by_priority?.URGENT || 0} /{" "}
                    {stats.tickets_by_priority?.URGENT || 0}
                  </p>
                </div>
              </div>

              {/* High */}
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">High</p>
                  <p className="text-lg font-bold text-yellow-600">
                    {stats.open_tickets_by_priority?.HIGH || 0} /{" "}
                    {stats.tickets_by_priority?.HIGH || 0}
                  </p>
                </div>
              </div>

              {/* Medium */}
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ClockIcon className="h-4 w-4 text-blue-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Medium</p>
                  <p className="text-lg font-bold text-blue-600">
                    {stats.open_tickets_by_priority?.MEDIUM || 0} /{" "}
                    {stats.tickets_by_priority?.MEDIUM || 0}
                  </p>
                </div>
              </div>

              {/* Low */}
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <ClockIcon className="h-4 w-4 text-gray-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Low</p>
                  <p className="text-lg font-bold text-gray-600">
                    {stats.open_tickets_by_priority?.LOW || 0} /{" "}
                    {stats.tickets_by_priority?.LOW || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Half - Open Tickets by Status */}
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Total tickets by Source
            </h3>
            <div className="flex flex-wrap gap-4">
              {/* COMPANY PORTAL */}
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <BuildingOfficeIcon className="h-4 w-4 text-indigo-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Company Portal</p>
                  <p className="text-lg font-bold text-red-600">
                    {stats.tickets_by_source?.COMPANY_PORTAL || 0}
                  </p>
                </div>
              </div>

              {/* LANDING PAGE */}
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <GlobeAltIcon className="h-4 w-4 text-sky-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Landing Page</p>
                  <p className="text-lg font-bold text-orange-600">
                    {stats.tickets_by_source?.LANDING_PAGE || 0}
                  </p>
                </div>
              </div>

              {/* JOB SEEKER PORTAL */}
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <UserCircleIcon className="h-4 w-4 text-purple-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Job Seeker Portal</p>
                  <p className="text-lg font-bold text-yellow-600">
                    {stats.tickets_by_source?.JOB_SEEKER_PORTAL || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Source and Category Breakdown for Open Tickets */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Half - Open Tickets by Source */}
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Total tickets by Status
            </h3>
            <div className="flex flex-wrap gap-4">
              {/* Open */}
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <ExclamationTriangleIcon className="h-4 w-4 text-green-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Open</p>
                  <p className="text-lg font-bold text-green-600">
                    {stats.open_tickets}
                  </p>
                </div>
              </div>

              {/* In Progress */}
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ClockIcon className="h-4 w-4 text-blue-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">In Progress</p>
                  <p className="text-lg font-bold text-blue-600">
                    {stats.in_progress_tickets}
                  </p>
                </div>
              </div>

              {/* Waiting For Customer Tickets */}
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <EnvelopeIcon className="h-4 w-4 text-orange-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Waiting For Customer </p>
                  <p className="text-lg font-bold text-red-600">
                    {stats.waiting_for_customer_tickets}
                  </p>
                </div>
              </div>

              {/* Resolved Tickets */}
              <div className="flex items-center">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckBadgeIcon className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Resolved </p>
                  <p className="text-lg font-bold text-orange-600">
                    {stats.resolved_tickets}
                  </p>
                </div>
              </div>

              {/* Closed Tickets */}
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircleIcon className="h-4 w-4 text-green-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Closed </p>
                  <p className="text-lg font-bold text-orange-600">
                    {stats.closed_tickets}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Half - Open Tickets by Category */}
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Total tickets by Category
            </h3>
            <div className="flex flex-wrap gap-4">
              {/* Feature Request */}
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <LightBulbIcon className="h-4 w-4 text-yellow-500" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Feature Request</p>
                  <p className="text-lg font-bold text-green-600">
                    {stats.tickets_by_category?.FEATURE_REQUEST || 0}
                  </p>
                </div>
              </div>

              {/* Billing Support */}
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CreditCardIcon className="h-4 w-4 text-green-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Billing Support</p>
                  <p className="text-lg font-bold text-blue-600">
                    {stats.tickets_by_category?.BILLING_SUPPORT || 0}
                  </p>
                </div>
              </div>

              {/* General Inquiry */}
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 text-blue-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">General Inquiry</p>
                  <p className="text-lg font-bold text-red-600">
                    {stats.tickets_by_category?.GENERAL_INQUIRY || 0}
                  </p>
                </div>
              </div>

              {/* Pricing Question */}
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <CurrencyDollarIcon className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Pricing Question</p>
                  <p className="text-lg font-bold text-orange-600">
                    {stats.tickets_by_category?.PRICING_QUESTION || 0}
                  </p>
                </div>
              </div>

              {/* Enterprise Plan */}
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <BuildingOffice2Icon className="h-4 w-4 text-indigo-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Enterprise Plan</p>
                  <p className="text-lg font-bold text-green-600">
                    {stats.tickets_by_category?.ENTERPRISE_PLAN || 0}
                  </p>
                </div>
              </div>

              {/* Technical Support */}
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <WrenchScrewdriverIcon className="h-4 w-4 text-red-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Technical Support</p>
                  <p className="text-lg font-bold text-blue-600">
                    {stats.tickets_by_category?.TECHNICAL_SUPPORT || 0}
                  </p>
                </div>
              </div>

              {/* Account Issue */}
              <div className="flex items-center pr-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <UserCircleIcon className="h-4 w-4 text-blue-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Account Issue</p>
                  <p className="text-lg font-bold text-blue-600">
                    {stats.tickets_by_category?.ACCOUNT_ISSUE || 0}
                  </p>
                </div>
              </div>

              {/* Integration Help */}
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <LinkIcon className="h-4 w-4 text-green-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Integration Help</p>
                  <p className="text-lg font-bold text-blue-600">
                    {stats.tickets_by_category?.INTEGRATION_HELP || 0}
                  </p>
                </div>
              </div>

              {/* Others */}
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <EllipsisHorizontalCircleIcon className="h-4 w-4 text-gray-600" />
                </div>
                <div className="ml-2">
                  <p className="text-xs text-gray-600">Others</p>
                  <p className="text-lg font-bold text-blue-600">
                    {stats.tickets_by_category?.OTHER || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            {
              key: "all",
              label: "All Tickets",
              count: stats?.total_tickets ?? 0,
            },
            {
              key: "landing",
              label: "Landing Page",
              count: stats?.tickets_by_source?.["LANDING_PAGE"] ?? 0,
            },
            {
              key: "job_seekers",
              label: "Job Seekers",
              count: stats?.tickets_by_source?.["JOB_SEEKER_PORTAL"] ?? 0,
            },
            {
              key: "companies",
              label: "Companies",
              count: stats?.tickets_by_source?.["COMPANY_PORTAL"] ?? 0,
            },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key as any);
                setCurrentPage(1);
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
              <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex flex-1 items-center gap-2">
          {/* Search Input */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

            <input
              type="text"
              placeholder="Search tickets by number, subject, description, or contact..."
              value={searchInput}
              onChange={(e) => {
                const value = e.target.value;
                setSearchInput(value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && searchInput.trim()) {
                  setCurrentPage(1);
                  setSearchTerm(searchInput);
                }
              }}
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Search Button */}
          {!isSearchApplied ? (
            <button
              type="button"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              onClick={handleSearch}
            >
              Search
            </button>
          ) : (
            <button type="button" color="secondary" onClick={handleClear}>
              Clear
            </button>
          )}
          {/* <button
            type="button"
            onClick={() => {
              setCurrentPage(1);
              setSearchTerm(searchInput.trim());
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Search
          </button> */}
        </div>

        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="OPEN">Open</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="WAITING_FOR_CUSTOMER">Waiting for Customer</option>
            <option value="RESOLVED">Resolved</option>
            <option value="CLOSED">Closed</option>
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => {
              setPriorityFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Priority</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="URGENT">Urgent</option>
            <option value="CRITICAL">Critical</option>
          </select>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source
              </label>
              <select
                value={sourceFilter}
                onChange={(e) => {
                  setSourceFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Sources</option>
                <option value="LANDING_PAGE">Landing Page</option>
                <option value="JOB_SEEKER_PORTAL">Job Seeker Portal</option>
                <option value="COMPANY_PORTAL">Company Portal</option>
                <option value="EMAIL">Email</option>
                <option value="PHONE">Phone</option>
                <option value="CHAT">Chat</option>
                <option value="INTERNAL">Internal</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Category</option>
                <option value="GENERAL_INQUIRY">General Inquiry</option>
                <option value="TECHNICAL_SUPPORT">Technical Support</option>
                <option value="BILLING_SUPPORT">Billing Support</option>
                <option value="FEATURE_REQUEST">Feature Request</option>
                <option value="BUG_REPORT">Bug Report</option>
                <option value="ACCOUNT_ISSUE">Account Issue</option>
                <option value="INTEGRATION_HELP">Integration Help</option>
                <option value="PRICING_QUESTION">Pricing Question</option>
                <option value="ENTERPRISE_PLAN">Enterprise Plan</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assignee
              </label>

              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={assigneeInput}
                  onChange={(e) => setAssigneeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && assigneeInput.trim()) {
                      setCurrentPage(1);
                      setAssigneeSearch(assigneeInput);
                    }
                  }}
                  placeholder="Search by assignee..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                {assigneeInput.trim() && (
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentPage(1);
                      setAssigneeSearch(assigneeInput);
                    }}
                    className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-600 text-white hover:bg-blue-700"
                    title="Search"
                  >
                    <MagnifyingGlassIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Created After */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created After
                </label>

                <input
                  type="date"
                  value={createdAfter}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCreatedAfter(value);
                    if (createdBefore && createdBefore < value) {
                      setCreatedBefore("");
                    }
                    setCurrentPage(1);
                  }}
                  max={createdBefore || undefined}
                  className="w-full rounded-md border border-gray-300 px-3 py-2
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created Before
                </label>

                <input
                  type="date"
                  value={createdBefore}
                  onChange={(e) => {
                    setCreatedBefore(e.target.value);
                    setCurrentPage(1);
                  }}
                  min={createdAfter || undefined}
                  className="w-full rounded-md border border-gray-300 px-3 py-2
                     focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                id="showOverdueTickets"
                type="checkbox"
                checked={showOverdueTickets}
                onChange={(e) => {
                  setShowOverdueTickets(e.target.checked);
                  setCurrentPage(1);
                }}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />

              <label
               htmlFor="showOverdueTickets"
               className="ml-2 text-sm font-medium text-gray-700 cursor-pointer"
               >
               Show overdue tickets
             </label>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => {
                setStatusFilter("");
                setPriorityFilter("");
                setSearchTerm("");
                setSearchInput("");
                setAssigneeInput("");
                setSourceFilter("");
                setCategoryFilter("");
                setCreatedAfter("");
                setCreatedBefore("");
                setAssigneeSearch("");
                setShowOverdueTickets(false);
              }}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Tickets Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      filteredTickets.length > 0 &&
                      selectedTickets.length === filteredTickets.length
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTickets(filteredTickets.map((t) => t.id));
                      } else {
                        setSelectedTickets([]);
                      }
                    }}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ticket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assignee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTickets.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={selectedTickets.includes(ticket.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTickets([...selectedTickets, ticket.id]);
                        } else {
                          setSelectedTickets(
                            selectedTickets.filter((id) => id !== ticket.id),
                          );
                        }
                      }}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {ticket.ticket_number}
                    </div>
                    {ticket.reply_count > 0 && (
                      <div className="text-xs text-gray-500">
                        {ticket.reply_count} replies
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {ticket.contact_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {ticket.contact_email}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div
                      className="text-sm text-gray-900 max-w-xs truncate"
                      title={ticket.subject}
                    >
                      {ticket.subject}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">
                      {ticket.category.replace("_", " ").toLowerCase()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(
                        ticket.priority,
                      )}`}
                    >
                      {getPriorityIcon(ticket.priority)}
                      <span className="ml-1">{ticket.priority}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(
                        ticket.status,
                      )}`}
                    >
                      {getStatusIcon(ticket.status)}
                      <span className="ml-1">
                        {ticket.status.replace("_", " ")}
                      </span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-500">
                      {getSourceIcon(ticket.source)}
                      <span className="ml-2 capitalize">
                        {ticket.source.replace("_", " ").toLowerCase()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {ticket.assigned_to_email ? (
                      <div className="text-sm text-gray-900">
                        {ticket.assigned_to_email}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">
                        Unassigned
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{formatTimeSince(ticket.updated_at)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewTicket(ticket)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View ticket details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditTicket(ticket)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Edit ticket"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredTickets.length === 0 && (
          <div className="text-center py-12">
            <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No tickets found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        )}
      </div>

      {getAllTicketTotalCount > rowsPerPage && (
        <TablePagination
          currentPage={currentPage}
          totalCount={getAllTicketTotalCount}
          rowsPerPage={rowsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onRowsPerPageChange={(rows) => {
            setRowsPerPage(rows);
            setCurrentPage(1);
          }}
        />
      )}

      {/* Bulk Actions */}
      {selectedTickets.length > 0 && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              {selectedTickets.length} ticket
              {selectedTickets.length > 1 ? "s" : ""} selected
            </span>
            <div className="flex space-x-2">
              <button
                onClick={async () => {
                  clearAssigneTicketData();
                  await fetchAssigneeUsers();
                  setShowEditModal(true);
                  setShowAssignSelectedTicket(true);
                }}
                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
              >
                Assign
              </button>
              <button
                onClick={() => {
                  setShowCloseTicket(true);
                  setNotifyCreator(false);
                }}
                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
              >
                Close
              </button>
            </div>
            <button
              onClick={() => setSelectedTickets([])}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Close Ticket Modal */}
      {showCloseTicket && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-3/4 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Confirm Action
                </h3>
                <button
                  onClick={() => setShowCloseTicket(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                This action will close {selectedTickets.length} selected ticket
                {selectedTickets.length > 1 ? "s" : ""}. Are you sure you want
                to proceed?
              </label>
              <div className="flex items-center pt-4">
                <input
                  type="checkbox"
                  checked={notifyCreator}
                  onChange={(event) => setNotifyCreator(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Notify Creator of Updates
                </label>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCloseTicket(false);
                    setNotifyCreator(false);
                  }}
                  disabled={updatingTicket}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignCloseTicket}
                  disabled={updatingTicket}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updatingTicket ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </div>
                  ) : (
                    "Continue"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Ticket Modal */}
      {(showEditModal || selectedTicket) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="flex w-3/4 max-w-2xl max-h-[90vh] flex-col overflow-hidden rounded-lg bg-white shadow-xl">
              <div className="flex items-center justify-between border-b px-5 py-4 shrink-0">
                <h3 className="text-lg font-medium text-gray-900">
                  {showAssignSelectedTicket
                    ? `Assign Ticket${selectedTickets.length > 1 ? "s" : ""}`
                    : `Edit Ticket - ${selectedTicket?.ticket_number}`}
                </h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedTicket(null);
                    setShowAssignSelectedTicket(false);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 pr-6 space-y-4"
                   style={{ scrollbarGutter: "stable" }}
                   >
                {!showAssignSelectedTicket && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={editPriority}
                      onChange={(e) => setEditPriority(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option
                        value={SupportTicketPriority.LOW}
                        selected={
                          selectedTicket?.priority === SupportTicketPriority.LOW
                        }
                      >
                        Low
                      </option>
                      <option
                        value={SupportTicketPriority.MEDIUM}
                        selected={
                          selectedTicket?.priority ===
                          SupportTicketPriority.MEDIUM
                        }
                      >
                        Medium
                      </option>
                      <option
                        value={SupportTicketPriority.HIGH}
                        selected={
                          selectedTicket?.priority ===
                          SupportTicketPriority.HIGH
                        }
                      >
                        High
                      </option>
                      <option
                        value={SupportTicketPriority.URGENT}
                        selected={
                          selectedTicket?.priority ===
                          SupportTicketPriority.URGENT
                        }
                      >
                        Urgent
                      </option>
                      <option
                        value={SupportTicketPriority.CRITICAL}
                        selected={
                          selectedTicket?.priority ===
                          SupportTicketPriority.CRITICAL
                        }
                      >
                        Critical
                      </option>
                    </select>
                  </div>
                )}

                {!showAssignSelectedTicket && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option
                        value={SupportTicketStatus.OPEN}
                        selected={
                          selectedTicket?.status === SupportTicketStatus.OPEN
                        }
                      >
                        Open
                      </option>
                      <option
                        value={SupportTicketStatus.IN_PROGRESS}
                        selected={
                          selectedTicket?.status ===
                          SupportTicketStatus.IN_PROGRESS
                        }
                      >
                        In Progress
                      </option>
                      <option
                        value={SupportTicketStatus.WAITING_FOR_CUSTOMER}
                        selected={
                          selectedTicket?.status ===
                          SupportTicketStatus.WAITING_FOR_CUSTOMER
                        }
                      >
                        Waiting for Customer
                      </option>
                      <option
                        value={SupportTicketStatus.RESOLVED}
                        selected={
                          selectedTicket?.status ===
                          SupportTicketStatus.RESOLVED
                        }
                      >
                        Resolved
                      </option>
                      <option
                        value={SupportTicketStatus.CLOSED}
                        selected={
                          selectedTicket?.status === SupportTicketStatus.CLOSED
                        }
                      >
                        Closed
                      </option>
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Super Admin Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={editAssigneeRole}
                    onChange={(e) => setEditAssigneeRole(e.target.value)}
                    disabled={loadingAssigneeUsers || !!assigneeUsersError}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">
                      {loadingAssigneeUsers
                        ? "Loading roles..."
                        : assigneeRoleOptions.length > 0
                        ? "Select role"
                        : "No roles found"}
                    </option>
                    {assigneeRoleOptions.map((role) => (
                      <option key={role} value={role}>
                        {formatRoleLabel(role)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assignee <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={editAssigneeUserId}
                    onChange={(e) => setEditAssigneeUserId(e.target.value)}
                    disabled={loadingAssigneeUsers || !!assigneeUsersError}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    <option value="">
                      {loadingAssigneeUsers
                        ? "Loading assignees..."
                        : filteredAssigneeUsers.length > 0
                        ? "Select assignee"
                        : "No assignees found for this role"}
                    </option>
                    {filteredAssigneeUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {getAssigneeName(user)}
                      </option>
                    ))}
                  </select>
                  {assigneeUsersError && (
                    <p className="mt-1 text-sm text-red-600">
                      {assigneeUsersError}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assignee Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={editAssigneeEmail}
                    readOnly
                    placeholder="Select an assignee to view email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={editDueDate}
                    onChange={(e) => setEditDueDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {!showAssignSelectedTicket && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Internal Notes
                    </label>
                    <textarea
                      rows={3}
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Add internal notes..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {!showAssignSelectedTicket && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={notifyCreator}
                      onChange={(event) =>
                        setNotifyCreator(event.target.checked)
                      }
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label className="ml-2 block text-sm text-gray-700">
                      Notify Creator of Updates
                    </label>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pb-5 px-5">
                <button
                  onClick={() => {
                    setSelectedTicket(null);
                    setShowEditModal(false);
                    setShowAssignSelectedTicket(false);
                  }}
                  disabled={updatingTicket}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={
                    showAssignSelectedTicket
                      ? handleAssignCloseTicket
                      : handleUpdateTicket
                  }
                  disabled={updatingTicket}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updatingTicket ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Updating...
                    </div>
                  ) : (
                    "Update"
                  )}
                </button>
              </div>
            </div>
          </div>
        
      )}

      {/* View Ticket Modal */}
      {showViewModal && selectedTicket && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-3/4 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Ticket Details - {selectedTicket.ticket_number}
                </h3>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedTicket(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Contact Information
                  </h4>
                  <p>
                    <strong>Name:</strong> {selectedTicket.contact_name}
                  </p>
                  <p>
                    <strong>Email:</strong> {selectedTicket.contact_email}
                  </p>
                  <p>
                    <strong>Source:</strong>{" "}
                    {selectedTicket.source.replace("_", " ")}
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Ticket Information
                  </h4>
                  <p>
                    <strong>Priority:</strong>
                    <span
                      className={`ml-2 px-2 py-1 rounded text-xs ${getPriorityColor(
                        selectedTicket.priority,
                      )}`}
                    >
                      {selectedTicket.priority}
                    </span>
                  </p>
                  <p>
                    <strong>Status:</strong>
                    <span
                      className={`ml-2 px-2 py-1 rounded text-xs ${getStatusColor(
                        selectedTicket.status,
                      )}`}
                    >
                      {selectedTicket.status.replace("_", " ")}
                    </span>
                  </p>
                  <p>
                    <strong>Category:</strong>{" "}
                    {selectedTicket.category.replace("_", " ")}
                  </p>
                  <p>
                    <strong>Created:</strong>{" "}
                    {dateFormatDynamic(
                      selectedTicket.created_at,
                      DateFormats.dateTime,
                      true
                    )}
                  </p>
                  <p>
                    <strong>Due Date:</strong>{" "}
                    {selectedTicket.due_date
                      ? dateFormatDynamic(selectedTicket.due_date)
                      : "-"}
                  </p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Assignee Details
                  </h4>
                  {selectedTicket.assigned_to_email ? (
                    <>
                      <p>
                        <strong>Name:</strong>{" "}
                        {[
                          selectedTicket.assigned_first_name,
                          selectedTicket.assigned_last_name,
                        ]
                          .filter(Boolean)
                          .join(" ") || "N/A"}
                      </p>
                      <p>
                        <strong>Email:</strong>{" "}
                        {selectedTicket.assigned_to_email}
                      </p>
                      <p>
                        <strong>Role:</strong>{" "}
                        {selectedTicket.assigned_to_role
                          ? selectedTicket.assigned_to_role
                              .split("_")
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() +
                                  word.slice(1).toLowerCase(),
                              )
                              .join(" ")
                          : "N/A"}
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-500 italic">Not assigned yet</p>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <strong>Subject</strong>
                <p className="text-gray-900">{selectedTicket.subject}</p>
              </div>

              <div className="mt-4">
                <strong>Description</strong>
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-gray-900 ">
                    {selectedTicket.description}
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <strong>Replies</strong>
                <p className="text-gray-900">
                  {selectedTicket.reply_count} replies
                  {selectedTicket.last_reply_at && (
                    <span>
                      {" "}
                      • Last reply:{" "}
                      {dateFormatDynamic(
                        selectedTicket.last_reply_at,
                        DateFormats.dateTime,
                      )}
                    </span>
                  )}
                </p>
              </div>
              <div className="mt-6">
                <strong>Internal Notes</strong>
                <p className="text-gray-900">{selectedTicket.internal_notes}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={closeToast}
        />
      )}
    </div>
  );
};

export default SupportTickets;
